import './styles.css';

/**
 * Loads third-party analytics vendors, gated on cookie consent.
 *
 * Configuration is the `CONSENT_GATED` table near the bottom; everything above
 * it is the machinery that table drives.
 *
 * The shell only ships to SaaS, so there is one path: OneTrust decides, per
 * category. `thirdPartyEnabled` gates the whole thing. Segment's stub is
 * installed either way, because the app calls `analytics.track` unconditionally
 * and the stub's queue is what makes that safe.
 *
 * Two ways to run vendor code, and neither is a dynamic `import()`:
 *
 *   loadScript(url)   for a vendor's own library, which needs a real <script>
 *                     element: OneTrust reads `data-domain-script` off its own
 *                     tag.
 *   runSnippet(src)   for vendor boilerplate we ship, which needs sloppy mode.
 *                     `snippets/` holds verbatim vendor bytes reached only
 *                     through `?raw`; never import one.
 */
import gtmSnippet from './snippets/googleTagManager.js?raw';
import segmentSnippet from './snippets/segment.js?raw';

/**
 * OneTrust's default category taxonomy. The numbering is OneTrust's, not ours:
 * renaming a category in their admin does not renumber it here.
 *
 * All five are named even where a category gates nothing, so that "gates
 * nothing on purpose" and "was forgotten" do not look alike in the registry.
 * Acceptance is read from `window.OnetrustActiveGroups` — see `acceptedGroups`.
 */
export const ConsentGroup = {
  STRICTLY_NECESSARY: 'C0001',
  PERFORMANCE: 'C0002',
  FUNCTIONAL: 'C0003',
  TARGETING: 'C0004',
  SOCIAL_MEDIA: 'C0005',
} as const;
export type ConsentGroup = (typeof ConsentGroup)[keyof typeof ConsentGroup];

declare global {
  interface Window {
    DEBUG_ANALYTICS?: boolean;
    /** SaaS performance-analytics permission read by Netron and WebViz remotes. */
    thirdPartyAnalyticsOK?: boolean;
    ReadyToInitDatadog?: boolean;
    Optanon?: {
      /** Whether the visitor has answered the banner yet. */
      IsAlertBoxClosed(): boolean;
    };
    /** Ours, and not replaceable: see `installOptanonWrapper`. */
    OptanonWrapper?: () => void;
    /** Accepted categories, comma-delimited and comma-wrapped: `,C0001,C0002,`. */
    OnetrustActiveGroups?: string;
    beamer_config?: {product_id: string; lazy: boolean; button: boolean};
  }
}

// --- config ----------------------------------------------------------------

export type Env = {
  /** Selects the OneTrust tenant. */
  isProduction: boolean;
  /** Whether any third-party vendor may load at all. */
  thirdPartyAllowed: boolean;
};

/**
 * Read from `__FORGE_SHELL_CONFIG__.analytics`. Not the standalone app's
 * `window.CONFIG`: that global belongs to the wandb remote and describes the
 * remote's deployment, not the shell's.
 *
 * An unconfigured deployment gets nothing: no vendors, and the `-test` OneTrust
 * tenant, so a missing config cannot write consent records to production.
 */
function readEnv(): Env {
  const config = window.__FORGE_SHELL_CONFIG__?.analytics ?? {};
  return {
    isProduction: config.environmentName === 'production',
    thirdPartyAllowed: config.thirdPartyEnabled === true,
    ...envOverride,
  };
}

/** Test seam: stands in for the configuration the deployment publishes. */
let envOverride: Partial<Env> | undefined;

export function setAnalyticsEnvForTests(override: Partial<Env>): void {
  envOverride = override;
}

function debugLog(...args: unknown[]): void {
  if (window.DEBUG_ANALYTICS) console.log('[analytics]', ...args);
}

// --- running vendor code ---------------------------------------------------

/** Deduped by url: the anonymous path can run more than once per page. */
const inFlight = new Map<string, Promise<HTMLScriptElement>>();

function loadScript(
  src: string,
  attributes?: Record<string, string>
): Promise<HTMLScriptElement> {
  const existing = inFlight.get(src);
  if (existing) return existing;

  const pending = new Promise<HTMLScriptElement>((resolve, reject) => {
    const script = document.createElement('script');
    // Attributes before `src`: a vendor that reads its own tag reads it while
    // executing, the way OneTrust reads `data-domain-script`.
    for (const [key, value] of Object.entries(attributes ?? {})) {
      script.setAttribute(key, value);
    }
    script.async = true;
    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    script.src = src;
    document.body.appendChild(script);
  });
  pending.catch(() => inFlight.delete(src));
  inFlight.set(src, pending);
  return pending;
}

/**
 * Classic-script semantics: sloppy mode, global scope, synchronous. ES modules
 * are always strict, and vitest cannot catch the difference because it
 * evaluates module bodies non-strict, so snippets stay `?raw` text.
 *
 * `new Function` rather than direct `eval`, which would run in this module's
 * scope.
 */
export function runSnippet(name: string, source: string): void {
  debugLog(`running ${name} snippet`);
  // oxlint-disable-next-line no-new-func
  new Function(source)();
}

// --- OneTrust --------------------------------------------------------------

const OT_DOMAIN = '01a0cee2-bd92-7bd2-9b05-d741ed11edfb';

function loadOneTrust(env: Env): Promise<unknown> {
  const domainId = env.isProduction ? OT_DOMAIN : `${OT_DOMAIN}-test`;
  return loadScript(`https://cdn.cookielaw.org/scripttemplates/otSDKStub.js`, {
    type: 'text/javascript',
    charset: 'UTF-8',
    'data-domain-script': domainId,
  });
}

/**
 * Which categories the visitor accepted.
 *
 * `OnetrustActiveGroups` is a plain string the SDK publishes — `,C0001,C0002,` —
 * rewritten on every consent save, immediately before the SDK dispatches
 * `consent.onetrust`. Reading it is synchronous and unambiguous.
 *
 * NOT `Optanon.InsertHtml(...)`, which OneTrust's docs steer you toward and
 * which reads like a consent gate. Checked against otBannerSdk.js 5.11.0, it
 * injects a node per category, and signals refusal by doing nothing, so
 * "declined" cannot be told apart from "has not answered yet".
 *
 * It also fails open on an unknown category id. Its `canInsertForGroup`
 * reduces to `groupIsAccepted || !groupExistsInOneTrustConfig`, so a renamed or
 * typo'd id loads that category's vendors for every visitor, including one who
 * declined everything. Reading the string treats a missing id as declined:
 * wrong in the opposite direction, and with a reportable symptom.
 */
function acceptedGroups(): Set<string> {
  const raw = window.OnetrustActiveGroups ?? '';
  return new Set(raw.split(',').filter(Boolean));
}

/**
 * OneTrust calls `window.OptanonWrapper()` on every page load and every save,
 * and anyone may assign it. We own the slot with a non-configurable accessor:
 * reads return ours, and assignments subscribe to ours instead of replacing it.
 */
const wrapperSubscribers = new Set<() => void>();
let wrapperRunning = false;

function notifyOptanonSubscribers(): void {
  /*
    The usual chaining recipe, `const prev = window.OptanonWrapper;
    window.OptanonWrapper = () => { prev(); mine(); }`, subscribes a function
    that calls us back synchronously. Without this guard it recurses forever.
  */
  if (wrapperRunning) return;
  wrapperRunning = true;
  for (const subscriber of wrapperSubscribers) {
    try {
      subscriber();
    } catch (error) {
      console.error('[analytics] OptanonWrapper subscriber threw', error);
    }
  }
  wrapperRunning = false;
}

/** Returns whether the slot is ours, so assigning it subscribes. */
function installOptanonWrapper(): boolean {
  if (window.OptanonWrapper === notifyOptanonSubscribers) return true;
  const existing = Object.getOwnPropertyDescriptor(window, 'OptanonWrapper');
  if (existing && !existing.configurable) {
    // Someone locked the slot first. The `consent.onetrust` listener still
    // covers consent saves; page loads go unapplied, so say so.
    console.error(
      '[analytics] OptanonWrapper is locked; consent on load is lost'
    );
    return false;
  }
  // Assigned before we got here: keep it, the same as a later assignment.
  if (typeof existing?.value === 'function') {
    wrapperSubscribers.add(existing.value);
  }
  Object.defineProperty(window, 'OptanonWrapper', {
    configurable: false,
    enumerable: true,
    get: () => notifyOptanonSubscribers,
    set: (fn: unknown) => {
      if (typeof fn === 'function' && fn !== notifyOptanonSubscribers) {
        wrapperSubscribers.add(fn as () => void);
      }
    },
  });
  return true;
}

// --- vendors ---------------------------------------------------------------

/*
  The same two Segment sources the standalone app writes to, chosen by the same
  rule (`enableSegmentAnalytics` in frontends/app's
  consentAwareAnalyticsInjector.js): production goes to the wandb.ai source,
  which forwards to Mixpanel and Chameleon; everything else goes to the dev
  source. Keeping one source per environment keeps shell and standalone
  sessions in the same Mixpanel project.

  The production source's settings route analytics.js through
  analytics.wandb.ai/v1 (its `apiHost`), not api.segment.io.
*/
const SEGMENT_WRITE_KEY_PRODUCTION = 'WmJvIs1MTqNjKAeQmEyw6TvqyRI5Su2z'; // gitleaks:allow
const SEGMENT_WRITE_KEY_DEVELOPMENT = 'FDppQ6oWt2M4gWarXmIdanSQmj3BjPkl'; // gitleaks:allow

function segmentWriteKey(e: Env): string {
  return e.isProduction
    ? SEGMENT_WRITE_KEY_PRODUCTION
    : SEGMENT_WRITE_KEY_DEVELOPMENT;
}

const vendors = {
  async segment(e: Env) {
    // The CDN templates the write key into the library it serves for that key.
    await loadScript(
      `https://cdn.segment.com/analytics.js/v1/${segmentWriteKey(e)}/analytics.min.js`
    );
  },

  /*
    Consent Mode defaults live in the snippet, ahead of the container loader,
    and they have to stay there. Two things break if they move into TypeScript:

      - Order. The defaults must reach `dataLayer` before gtm.js does, which is
        what `wait_for_update` is timed against. Calling `runSnippet` first puts
        the container in front of them.
      - Shape. `gtag` pushes its `arguments` object, and GTM's consent API only
        recognises that one entry. A rest-and-spread reimplementation
        (`push(...args)`) writes three loose entries — "consent", "default",
        {...} — which GTM ignores, silently losing the whole signal.

    The values themselves are ours to change; see `snippets/README.md`.
  */
  googleTagManager: () => runSnippet('googleTagManager', gtmSnippet),

  webSights: () =>
    loadScript('https://ws.zoominfo.com/pixel/qhXboAuxB56YVRvedMdX'),

  async beamer() {
    // Must exist before the embed runs; the embed reads it at load.
    // The header's "What's new" button opens the feed; no floating button.
    window.beamer_config = {
      product_id: 'iTpiKrhl12143',
      lazy: true,
      button: false,
    };
    await loadScript('https://app.getbeamer.com/js/beamer-embed.js');
  },

  /** Loads nothing — the app polls this flag and calls initDatadog itself. */
  datadog: () => {
    window.ReadyToInitDatadog = true;
  },
} satisfies Record<string, (env: Env) => void | Promise<unknown>>;

type VendorName = keyof typeof vendors;

// --- the tables ------------------------------------------------------------

/**
 * Moving a vendor between categories changes what a visitor's click consents
 * to, so this is the compliance-relevant edit in this file.
 *
 * Not the full inventory: Segment fetches its own device-mode destinations at
 * runtime (Chameleon arrives that way), and those inherit C0002 by only running
 * once Segment does.
 */
const CONSENT_GATED: Partial<Record<ConsentGroup, readonly VendorName[]>> = {
  [ConsentGroup.PERFORMANCE]: [
    // 'googleTagManager',
    'segment',
    'webSights',
    'datadog',
  ],
  [ConsentGroup.FUNCTIONAL]: ['beamer'],
};

// --- boot ------------------------------------------------------------------

const installed = new Set<VendorName>();
const granted = new Set<ConsentGroup>();
let firstConsentPass: Promise<unknown> | undefined;

/**
 * Held only so `resetAnalyticsForTests` can detach it. In a real page the
 * listener should outlive everything: it is one way a visitor who changes
 * their mind later gets their scripts.
 */
let consentListener: (() => void) | undefined;

/**
 * Module-private rather than a `window` flag, which any vendor script this
 * loader pulls in could clear — GTM's Custom HTML tags among them.
 */
let booted = false;
let env: Env | undefined;

async function install(name: VendorName, e: Env): Promise<void> {
  if (installed.has(name)) return;
  installed.add(name);
  try {
    // Widened deliberately: most vendors ignore the environment, so the union
    // of their signatures takes no argument and calling with one is an error.
    const run: (env: Env) => void | Promise<unknown> = vendors[name];
    await run(e);
    debugLog(`${name} initialized`);
  } catch (error) {
    // One vendor's CDN being down must not stop the others.
    console.error(`[analytics] ${name} failed to load`, error);
  }
}

/**
 * One pass over every gated category.
 *
 * OneTrust decides for every visitor, signed in or not. Accepting the terms of
 * service is not cookie consent: it is not specific to a category and it is not
 * revocable in the way consent has to be, so the banner is shown to everyone
 * and the answer is honoured for everyone.
 */
function applyConsent(e: Env): Promise<unknown> {
  const accepted = acceptedGroups();
  debugLog(
    'active groups',
    [...accepted].join(',') || '(none)',
    window.Optanon?.IsAlertBoxClosed() ? '(answered)' : '(banner still open)'
  );
  return Promise.all(
    Object.entries(CONSENT_GATED).map(([group, names]) => {
      if (granted.has(group as ConsentGroup)) return undefined;
      if (!accepted.has(group)) return undefined;
      granted.add(group as ConsentGroup);
      if (group === ConsentGroup.PERFORMANCE) {
        // See the note where this is initialised: it tracks vendors actually
        // running, which is what the remote's embedded viewers key off.
        window.thirdPartyAnalyticsOK = true;
      }
      debugLog(`${group} granted`, names.join(', '));
      return Promise.all(names.map(name => install(name, e)));
    })
  );
}

/**
 * Loads OneTrust and installs whatever the visitor consented to.
 *
 * Takes no authentication state: the banner is shown and honoured for signed-in
 * visitors too. Never resolves if OneTrust itself fails to load, which is why
 * callers do not await it.
 */
export function initializeTrackingScripts(): Promise<unknown> {
  const e = env ?? (env = readEnv());

  // The caller has no idea the deployment turned analytics off; without this it
  // would load OneTrust and every consented vendor once the viewer resolved.
  if (!e.thirdPartyAllowed) {
    return Promise.resolve();
  }

  if (!firstConsentPass) {
    firstConsentPass = new Promise<unknown>(resolve => {
      /*
        Two signals, both registered before OneTrust is requested so the first
        cannot be missed:

          OptanonWrapper     called on every page load and every save. The
                             primary signal; see `installOptanonWrapper`.
          consent.onetrust   dispatched on every save, but on page load only
                             when the visitor's geolocation rule has Google
                             Consent Mode enabled (`GCEnable`). The SDK skips
                             it otherwise, so a visitor who never clicks the
                             banner, or answered on an earlier visit, never
                             gets it. Kept because a listener cannot be
                             displaced, whatever happens to the wrapper.

        Not `OneTrustGroupsUpdated` either: on load it fires only when there is
        no consent cookie yet, so it misses returning visitors.

        A save delivers both; `applyConsent` skips categories already granted,
        so the second pass does nothing. Only the first signal settles the
        promise; the pass itself runs on every consent change.
      */
      consentListener = () => {
        resolve(applyConsent(e));
      };
      if (installOptanonWrapper()) window.OptanonWrapper = consentListener;
      window.addEventListener('consent.onetrust', consentListener);
    });
    void loadOneTrust(e).catch(error =>
      console.error('[analytics] OneTrust failed to load', error)
    );
  }

  // Memoized: re-running re-inserts `otSDKStub.js` and can re-present the
  // banner to someone who already answered. Signing in or out must not redo it.
  return firstConsentPass;
}

export function bootAnalytics(): void {
  if (booted) return;
  booted = true;

  // Published as `false` rather than left undefined so it is discoverable by
  // poking at `window` in a console.
  window.DEBUG_ANALYTICS ??= false;

  const e = (env = readEnv());
  /*
    False until the category is granted, not merely permitted by config. The
    remote's embedded viewers read this to decide whether to pass `telemetry=0`
    to Netron and WebViz, so `true` at boot would opt a visitor into third-party
    telemetry before they answered the banner.
  */
  window.thirdPartyAnalyticsOK = false;
  debugLog('environment', e);

  /*
    Even when third-party analytics are off: the app calls `analytics.track`
    without checking, and the stub's queue is what keeps that from throwing.
    Nothing leaves the page until the library loads, which it never does when
    analytics are off.
  */
  runSnippet('segment', segmentSnippet);
  // The snippet embeds the production key; outside production the stub has to
  // agree with the library `vendors.segment` loads.
  const stub = window.analytics as {_writeKey?: string} | undefined;
  if (stub) stub._writeKey = segmentWriteKey(e);

  // Vendors wait for `initializeTrackingScripts`, which the shell calls once it
  // knows the viewer.
  if (!e.thirdPartyAllowed) {
    debugLog('analytics disabled');
  }
}

/** Test seam: all of the above is module state. */
export function resetAnalyticsForTests(): void {
  if (consentListener != null) {
    window.removeEventListener('consent.onetrust', consentListener);
    consentListener = undefined;
  }
  // The property itself outlives this: it is non-configurable.
  wrapperSubscribers.clear();
  booted = false;
  envOverride = undefined;
  inFlight.clear();
  installed.clear();
  granted.clear();
  firstConsentPass = undefined;
  env = undefined;
}
